import { ndash } from '@ipfabric/frontend-shared-assets/utils/characters';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Security, safeAbsurd } from '@ipfabric/utils';
import { Fragment, type ReactNode } from 'react';
import type { AppTranslations } from 'translations/translations';

export const renderSecurityValue = (
  msgSimpleTest: AppTranslations['securitySchema']['test']['simple'],
  value?: Security.LiteralValue,
): ReactNode => {
  if (value === undefined) {
    stumble('Unable to dereference value');
    return <em className='text-danger'>{msgSimpleTest.value.error}</em>;
  }
  if (value.original) {
    // this field is considered to be a "human-readable variant" if present
    return value.original;
  }

  if (value.type === 'boolean') {
    return msgSimpleTest.value.boolean[value.value ? 'true' : 'false'];
  }

  if (value.type === 'number') {
    return value.value;
  }

  if (value.type === 'numberArray') {
    return value.value.join(', ');
  }

  if (value.type === 'numberSet') {
    return value.value.map(({ min, max }, index) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: no better alternative
      <Fragment key={index}>
        {index > 0 && <br />}
        {`[${min} ${ndash} ${max}]`}
      </Fragment>
    ));
  }

  if (value.type === 'string') {
    return value.value;
  }

  if (value.type === 'stringArray') {
    return value.value.map((str, index) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: no better alternative
      <Fragment key={index}>
        {index > 0 && <br />}
        {str}
      </Fragment>
    ));
  }

  if (value.type === 'stringSet') {
    return value.value.map(({ min, max }, index) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: no better alternative
      <Fragment key={index}>
        {index > 0 && <br />}
        {`[${min} ${ndash} ${max}]`}
      </Fragment>
    ));
  }

  if (value.type === 'bitMaskCheck') {
    return value.original;
  }

  if (value.type === 'numberSetGroup') {
    // NIM-10765: we need UI design for NumberSetGroupValue type
    return value.original;
  }

  if (value.type === 'stringSetGroup') {
    return value.original;
  }

  safeAbsurd(value);
  return null;
};

/**
 * `renderSecurityValue` with run-time handling that the value was dereferenced
 *
 * Use where it is reasonable to share type with original definition rather than complex
 * type rewrite.
 * In other cases, prefer using `renderSecurityValue` where you have Security.LiteralValue
 * so type is rather covered by type checks.
 */
export const renderSecurityValueEnsureDereferenced = (
  msgSimpleTest: AppTranslations['securitySchema']['test']['simple'],
  value?: Security.Value,
): ReactNode => {
  if (Security.isReferencedValue(value)) {
    stumble('Got referenced value where literal was expected');
    return <em className='text-danger'>{msgSimpleTest.value.error}</em>;
  }
  return renderSecurityValue(msgSimpleTest, value);
};
